import {
  AssistantMultiStepFormAnswer,
  AssistantMultiStepFormStep,
  AssistantProcessStatus,
  Document,
} from "@models";
import {
  getDocumentAndVersionForDataKeyId,
  getDocumentAnswerValueByDataKeyId,
  getElementConfigByDataKey,
  getTemplateKeyForDataKeyId,
  isRevision,
} from "@utils";
import { makeAutoObservable } from "mobx";
import { getAssistantProcessAsync } from "src/hooks/queries/assistant-process";
import { useUpdateAssistantProcessMutation } from "src/hooks/queries/assistant-process-mutations";
import {
  getDeviceSystemDataKeyPayload,
  getOrganizationSystemDataKeyPayload,
  isDeviceSystemDataKey,
  isOrganizationSystemDataKey,
} from "src/utils/system-data-keys";
import { RootStoreType } from ".";
import {
  useCreateDocumentMutation,
  useCreateDocumentVersionMutation,
  useSaveAnswerMutation,
  useUpdateDeviceMutation,
  useUpdateOrgMutation,
} from "../hooks/mutations";
import { TemplateConfig } from "../types";

export interface ToolActionEvent {
  id: string;
  type: "data-key-apply" | "other-tool-action" | "prompt-inject";
  payload: any;
  timestamp: number;
}

export interface DataKeyApplyEvent extends ToolActionEvent {
  type: "data-key-apply";
  payload: {
    dataKeyId: string;
    value: string;
    currentValue?: string; // Store current value for diff calculation
  };
}

export interface PromptInjectEvent extends ToolActionEvent {
  type: "prompt-inject";
  payload: PromptInjectPayload;
}

export type PromptInjectPayload = {
  prompt: string;
  preview?: string; // Short user-friendly preview text
  source?: string; // Optional identifier for the source component
  replaceInput?: boolean; // Whether to replace current input or append
  submit?: boolean; // Whether to auto-submit after injecting the prompt
  newThread?: boolean; // Start a new thread and reset its composer before injecting
  // Additional context data for ChatInputWithContext
  contextConfig?: Map<any, any>;
  device?: any;
  documents?: any;
  user?: any;
  deviceId?: string;
  templatesConfig?: any;
  skillContexts?: import("src/components/Chat/types").ChatSkillContext[];
};

// Store current context for data keys
export interface DataKeyContext {
  dataKeyId: string;
  currentValue: string;
  timestamp: number;
}

export class ToolActionsStore {
  rootStore;

  // Store the latest apply events for each data key
  private _dataKeyApplyEvents = new Map<string, DataKeyApplyEvent>();

  // Store pending actions that haven't been handled yet
  private _pendingActions: ToolActionEvent[] = [];

  // Store the latest prompt inject events
  private _promptInjectEvents: PromptInjectEvent[] = [];

  constructor(rootStore: RootStoreType) {
    makeAutoObservable(this, { rootStore: false }, { autoBind: true });
    this.rootStore = rootStore;
  }

  // Save the applied value and emit an event to notify the components that the data key has been applied for instant update in the local wizard state
  async applyDataKey({
    orgId,
    deviceId,
    dataKeyId,
    value,
    documents,
    templatesConfig,
    saveAnswerMutation,
    createDocumentVersionMutationAsync,
    createDocumentMutationAsync,
    updateOrgMutationAsync,
    updateDeviceMutationAsync,
    updateAssistantProcessMutationAsync,
  }: {
    orgId: string;
    deviceId: string;
    dataKeyId: string;
    value: string;
    documents: Document[];
    templatesConfig: TemplateConfig;
    saveAnswerMutation: ReturnType<typeof useSaveAnswerMutation>["mutate"];
    createDocumentVersionMutationAsync: ReturnType<
      typeof useCreateDocumentVersionMutation
    >["mutateAsync"];
    createDocumentMutationAsync: ReturnType<
      typeof useCreateDocumentMutation
    >["mutateAsync"];
    updateOrgMutationAsync?: ReturnType<
      typeof useUpdateOrgMutation
    >["mutateAsync"];
    updateDeviceMutationAsync?: ReturnType<
      typeof useUpdateDeviceMutation
    >["mutateAsync"];
    updateAssistantProcessMutationAsync: ReturnType<
      typeof useUpdateAssistantProcessMutation
    >["mutateAsync"];
  }) {
    if (isOrganizationSystemDataKey(dataKeyId)) {
      if (!updateOrgMutationAsync) {
        throw new Error(
          `Missing organization mutation for system data key ${dataKeyId}`
        );
      }

      await updateOrgMutationAsync(
        getOrganizationSystemDataKeyPayload({
          dataKeyId,
          value,
        })
      );
      return;
    }

    if (isDeviceSystemDataKey(dataKeyId)) {
      if (!updateDeviceMutationAsync) {
        throw new Error(
          `Missing device mutation for system data key ${dataKeyId}`
        );
      }

      await updateDeviceMutationAsync({
        data: getDeviceSystemDataKeyPayload({
          dataKeyId,
          value,
        }),
      });
      return;
    }

    // notify the components that the data key has been applied
    const event: DataKeyApplyEvent = {
      id: `${dataKeyId}-${Date.now()}`,
      type: "data-key-apply",
      payload: { dataKeyId, value },
      timestamp: Date.now(),
    };

    this._dataKeyApplyEvents.set(dataKeyId, event);
    this._pendingActions.push(event);

    // check if we have a change management process running and if this data key is part of it save it the the change management process state instead of in an anser in a document version. But only if we are currently also in a change management url (assistant/document-update)
    const pathname = window.location.pathname;
    const isChangeManagementUrl = pathname.includes(
      "/assistant/document-update"
    );

    if (isChangeManagementUrl) {
      // get the change management process
      const assistantProcesses = await getAssistantProcessAsync({
        orgId,
        deviceId,
        status: AssistantProcessStatus.IN_PROGRESS,
      });
      if (assistantProcesses && assistantProcesses.length > 0) {
        // get the change management process
        const assistantProcess = assistantProcesses[0];
        // check if the data key is included in the dataKeysToUpdate array
        const dataKeyIndex = assistantProcess.state.dataKeysToUpdate.findIndex(
          (dataKey: AssistantMultiStepFormStep) =>
            dataKey.dataKeyId === dataKeyId
        );
        if (dataKeyIndex !== -1) {
          // update the value if already in changes. otherwise add it to the changes array
          const changeIndex = assistantProcess.state.changes.findIndex(
            (change: AssistantMultiStepFormAnswer) =>
              change.dataKeyId === dataKeyId
          );
          if (changeIndex !== -1) {
            // update the value if existent
            assistantProcess.state.changes[changeIndex].acceptedAnswer = {
              ...assistantProcess.state.changes[changeIndex].acceptedAnswer,
              answer: value,
            };
          } else {
            // get the current value from the document
            const currentValue = getDocumentAnswerValueByDataKeyId(
              dataKeyId,
              documents
            );

            // a new change otherwise
            assistantProcess.state.changes.push({
              dataKeyId,
              templateKey: getTemplateKeyForDataKeyId(
                dataKeyId,
                templatesConfig
              ),
              previousAnswer: { answer: currentValue },
              acceptedAnswer: { answer: value },
              suggestion: undefined,
            });
          }

          // save the change management process
          await updateAssistantProcessMutationAsync({
            processId: assistantProcess.id,
            body: {
              state: assistantProcess.state,
            },
          });

          return;
        }
      }
    }

    // else run the normal flow below
    const { document, version } = getDocumentAndVersionForDataKeyId(
      dataKeyId,
      documents,
      templatesConfig
    );

    const step = getElementConfigByDataKey(dataKeyId, templatesConfig);

    // If we have a document and a version, we can save the answer
    if (document && version) {
      // if we have a revision we first need to create a new version since revisions are not editable
      if (isRevision(version)) {
        const newVersion = await createDocumentVersionMutationAsync({
          docId: document.id,
        });
        saveAnswerMutation({
          docId: document.id,
          documentVersion: newVersion,
          answer: value,
          deviceId,
          orgId,
          step,
        });
      } else {
        saveAnswerMutation({
          docId: document.id,
          documentVersion: version,
          answer: value,
          deviceId,
          orgId,
          step,
        });
      }
    } else if (document && !version) {
      // we need to create a new version first and then save the answer
      const newVersion = await createDocumentVersionMutationAsync({
        docId: document.id,
      });
      saveAnswerMutation({
        docId: document.id,
        documentVersion: newVersion,
        answer: value,
        deviceId,
        orgId,
        step,
      });
    } else if (!document) {
      // we need to create a new document first and then save the answer
      const newDocument = await createDocumentMutationAsync({
        orgId,
        body: {
          deviceId,
          templateKey: getTemplateKeyForDataKeyId(dataKeyId, templatesConfig),
        },
      });
      const newVersion = await createDocumentVersionMutationAsync({
        docId: newDocument.id,
      });
      saveAnswerMutation({
        docId: newDocument.id,
        documentVersion: newVersion,
        answer: value,
        deviceId,
        orgId,
        step,
      });
    }
  }

  // Get the latest apply event for a specific data key
  getLatestDataKeyApply(dataKeyId: string): DataKeyApplyEvent | undefined {
    return this._dataKeyApplyEvents.get(dataKeyId);
  }

  // Check if there's a pending apply for a data key
  hasPendingApply(dataKeyId: string): boolean {
    return this._pendingActions.some(
      (action) =>
        action.type === "data-key-apply" &&
        action.payload.dataKeyId === dataKeyId
    );
  }

  // Mark an apply event as handled (removes from pending)
  markApplyAsHandled(dataKeyId: string) {
    this._pendingActions = this._pendingActions.filter(
      (action) =>
        !(
          action.type === "data-key-apply" &&
          action.payload.dataKeyId === dataKeyId
        )
    );
  }

  // Clear all events for a data key (useful when navigating away)
  clearDataKeyEvents(dataKeyId: string) {
    this._dataKeyApplyEvents.delete(dataKeyId);
    // this._dataKeyContexts.delete(dataKeyId);

    // Clear all diff results for this dataKeyId (regardless of toolCallId)
    this._pendingActions = this._pendingActions.filter(
      (action) =>
        !(
          action.type === "data-key-apply" &&
          action.payload.dataKeyId === dataKeyId
        )
    );
  }

  // Clear all events (useful for cleanup)
  clearAllEvents() {
    this._dataKeyApplyEvents.clear();
    // this._dataKeyContexts.clear();
    this._pendingActions = [];
    this._promptInjectEvents = [];
  }

  // Get all pending actions (useful for debugging)
  get pendingActions() {
    return [...this._pendingActions];
  }

  // === PROMPT INJECTION METHODS ===

  // Emit a prompt inject event
  injectPrompt(options: PromptInjectPayload) {
    const event: PromptInjectEvent = {
      id: `prompt-inject-${Date.now()}`,
      type: "prompt-inject",
      payload: {
        prompt: options.prompt,
        preview: options.preview,
        source: options.source,
        replaceInput: options.replaceInput ?? false,
        submit: options.submit ?? false,
        newThread: options.newThread ?? false,
        contextConfig: options.contextConfig,
        device: options.device,
        documents: options.documents,
        user: options.user,
        deviceId: options.deviceId,
        templatesConfig: options.templatesConfig,
        skillContexts: options.skillContexts,
      },
      timestamp: Date.now(),
    };

    this._promptInjectEvents.push(event);
    this._pendingActions.push(event);
  }

  // Get all pending prompt inject events
  getPendingPromptInjects(): PromptInjectEvent[] {
    return this._promptInjectEvents.filter((event) =>
      this._pendingActions.some((pending) => pending.id === event.id)
    );
  }

  // Get the latest prompt inject event
  getLatestPromptInject(): PromptInjectEvent | undefined {
    const pendingPrompts = this.getPendingPromptInjects();
    return pendingPrompts[pendingPrompts.length - 1];
  }

  // Mark a prompt inject event as handled
  markPromptInjectAsHandled(eventId: string) {
    this._pendingActions = this._pendingActions.filter(
      (action) => action.id !== eventId
    );
  }

  // Check if there are pending prompt inject events
  hasPendingPromptInject(): boolean {
    return this._pendingActions.some(
      (action) => action.type === "prompt-inject"
    );
  }

  // Clear all prompt inject events
  clearPromptInjectEvents() {
    this._promptInjectEvents = [];
    this._pendingActions = this._pendingActions.filter(
      (action) => action.type !== "prompt-inject"
    );
  }
}
